import React, { useState, useEffect } from 'react';
import { useI18n, LANGUAGES } from '../contexts/I18nContext';
import { Button } from './ui/button';
import { Gamepad2, Globe, ChevronRight, Sparkles } from 'lucide-react';

export default function SplashScreen() {
  const { setLanguage, setShowSplash, detectedLanguage } = useI18n();
  // Pre-select the browser-detected language if supported; falls back to English.
  const [selectedLang, setSelectedLang] = useState(detectedLanguage || 'en');
  const languages = Object.entries(LANGUAGES);
  const [animationPhase, setAnimationPhase] = useState(0);

  useEffect(() => {
    // Staggered animation phases
    const timers = [
      setTimeout(() => setAnimationPhase(1), 300),
      setTimeout(() => setAnimationPhase(2), 800),
      setTimeout(() => setAnimationPhase(3), 1300),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  const handleContinue = () => {
    setLanguage(selectedLang);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-[#030305] flex items-center justify-center overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0">
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `
              linear-gradient(rgba(6, 182, 212, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(6, 182, 212, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px',
            transform: 'perspective(500px) rotateX(60deg)',
            transformOrigin: 'center top',
          }}
        />
        
        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#06B6D4]/20 rounded-full blur-[100px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-[100px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-emerald-500/10 rounded-full blur-[150px]" />
        
        {/* Floating particles */}
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#06B6D4] rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${3 + Math.random() * 4}s`,
            }}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-6 max-w-lg">
        {/* Logo */}
        <div 
          className={`transition-all duration-1000 ${animationPhase >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className="w-20 h-20 rounded-xl bg-gradient-to-br from-[#06B6D4] to-[#0891B2] flex items-center justify-center shadow-2xl shadow-[#06B6D4]/30 animate-float">
              <Gamepad2 className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-black tracking-tight text-white mb-2">
            <span className="bg-gradient-to-r from-white via-[#06B6D4] to-white bg-clip-text text-transparent">
              PlayMold
            </span>
          </h1>
          <div className="flex items-center justify-center gap-2 text-zinc-500 text-sm mt-4">
            <Sparkles className="w-4 h-4 text-[#06B6D4]" />
            <span>Powered by AI</span>
          </div>
        </div>

        {/* Language Selection */}
        <div 
          className={`mt-12 transition-all duration-1000 delay-300 ${animationPhase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <div className="flex items-center justify-center gap-2 mb-6 text-zinc-400">
            <Globe className="w-5 h-5" />
            <span className="text-sm font-medium">Select your language</span>
          </div>

          {detectedLanguage && (
            <p className="text-xs text-zinc-500 text-center mb-6 -mt-3" data-testid="splash-detected-lang">
              <span className="text-[#06B6D4]">Auto-detected: {LANGUAGES[detectedLanguage]?.flag} {LANGUAGES[detectedLanguage]?.name}</span>
              &nbsp;· You can change it below.
            </p>
          )}

          <div className="grid grid-cols-3 gap-2 max-w-md mx-auto mb-8">
            {languages.map(([code, { name, flag }]) => (
              <button
                key={code}
                onClick={() => setSelectedLang(code)}
                className={`
                  flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium
                  transition-all duration-200
                  ${selectedLang === code 
                    ? 'bg-[#06B6D4] text-black shadow-lg shadow-[#06B6D4]/30' 
                    : 'bg-white/5 text-zinc-300 hover:bg-white/10 border border-white/10'
                  }
                `}
              >
                <span className="text-lg">{flag}</span>
                <span className="hidden sm:inline">{name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Continue Button */}
        <div 
          className={`transition-all duration-1000 delay-500 ${animationPhase >= 3 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
        >
          <Button 
            size="lg"
            onClick={handleContinue}
            className="bg-gradient-to-r from-[#06B6D4] to-[#0891B2] text-white hover:opacity-90 rounded-full px-12 py-6 text-lg font-semibold shadow-xl shadow-[#06B6D4]/30 group"
          >
            Continue
            <ChevronRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Button>
          
          <p className="mt-8 text-xs text-zinc-600">
            © 2026 PlayMold. All rights reserved.
          </p>
        </div>
      </div>

      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.5; }
          50% { transform: translateY(-20px) scale(1.2); opacity: 1; }
        }
        .animate-float {
          animation: float 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
